import React from 'react';
import { useSelector } from 'react-redux';
import { pick } from 'rambdax';
import useSWR from 'swr';
import { RECENTLY_VIEWED_CONSTITUENTS } from '@/lib/constants/localStorageKeys';

const getConsFromStorage = () =>
  JSON.parse(localStorage.getItem(RECENTLY_VIEWED_CONSTITUENTS)) || [];

const useRecentlyViewedConstituents = () => {
  const currentOrgId = useSelector(
    (state) => state.authentication?.ownerOrgId || null,
  );

  // Grab constituents from local storage, but only return constituents that
  // match the current org ID.
  const { data, mutate } = useSWR(
    [currentOrgId, RECENTLY_VIEWED_CONSTITUENTS],
    ([ownerOrgId]) =>
      getConsFromStorage().filter((cons) => cons.ownerOrgId === ownerOrgId),
  );

  const recordConstituentView = React.useCallback(
    (constituent) => {
      const newConstituent = pick(
        [
          'constituentId',
          'constituentType',
          'displayName',
          'avatar',
          'ownerOrgId',
        ],
        constituent,
      );
      newConstituent.lastViewed = new Date().toISOString();

      // Update the list by adding the new constituent to the front of our
      // constituent array and removing any duplicates. We want to use all the
      // constituents from local storage rather than just the ones that have
      // been filtered by org ID in `data` to preserve recently viewed stage
      // for super admins switching between orgs.
      const updatedList = [
        newConstituent,
        ...getConsFromStorage().filter(
          (cons) => cons.constituentId !== newConstituent.constituentId,
        ),
      ].slice(0, 10);
      localStorage.setItem(
        RECENTLY_VIEWED_CONSTITUENTS,
        JSON.stringify(updatedList),
      );
      // Force SWR to revalidate.
      mutate();
    },
    [data, mutate],
  );

  const deleteConstituentFromView = React.useCallback(
    (constituentId) => {
      const updatedList = [
        ...getConsFromStorage().filter(
          (constituents) => constituents.constituentId !== constituentId,
        ),
      ].slice(0, 10);
      localStorage.setItem(
        RECENTLY_VIEWED_CONSTITUENTS,
        JSON.stringify(updatedList),
      );
      // Force SWR to revalidate.
      mutate();
    },
    [mutate],
  );

  return {
    constituents: data || [],
    deleteConstituentFromView,
    recordConstituentView,
  };
};

export default useRecentlyViewedConstituents;
